import { join } from 'node:path';
import { globby } from 'globby';
import type { WixConfig } from '@wix/cli-app-definitions';
import { randomUUID } from 'node:crypto';
import {
  DASHBOARD_MENU_PLUGINS_DIR,
  DASHBOARD_PAGES_DIR,
  DASHBOARD_PLUGINS_DIR,
  DASHBOARD_MODALS_DIR,
  PlatformType,
} from '@wix/cli-app-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { DevCenterComponent } from '../dev-center-component.js';
import {
  loadDashboardPluginComponentConfig,
  loadDashboardMenuPluginComponentConfig,
  loadDashboardModalComponentConfig,
  loadDashboardPageComponentConfig,
} from './config.js';
import { normalizeGlobbyPattern, deriveRoute } from '../path.js';
import { createDashboardRouteValidation } from '../validations/index.js';
import { readAppConfig } from '../../../files/app.config.js';
import { API_BASEURL_PLACEHOLDER } from '../../../hosting/upload-application.js';

export const DASHBOARD_PAGES_PATTERN = join(
  DASHBOARD_PAGES_DIR,
  '**',
  'page.{ts,tsx,js,jsx}'
);

export const DASHBOARD_PLUGINS_PATTERN = join(
  DASHBOARD_PLUGINS_DIR,
  '**',
  'plugin.{ts,tsx,js,jsx}'
);

const DASHBOARD_MENU_PLUGINS_PATTERN = join(
  DASHBOARD_MENU_PLUGINS_DIR,
  '**',
  'plugin.json'
);

export const DASHBOARD_MODALS_PATTERN = join(
  DASHBOARD_MODALS_DIR,
  '**',
  'modal.{ts,tsx,js,jsx}'
);

async function loadDashboardPageComponent(
  projectFolder: string,
  relativePath: string
): Promise<DevCenterComponent<'DashboardPage'>> {
  const pageConfig = await loadDashboardPageComponentConfig(relativePath);
  const route = deriveRoute(projectFolder, join(projectFolder, relativePath));

  return DevCenterComponent.DashboardPage({
    ...pageConfig,
    route,
    relativePath,
    platforms: [PlatformType.Dashboard()],
  });
}

export async function loadDashboardPageComponents(
  projectFolder: string
): Promise<Array<DevCenterComponent<'DashboardPage'>>> {
  const dashboardPagesPaths = await globby(
    normalizeGlobbyPattern(DASHBOARD_PAGES_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const dashboardPages = await Promise.all(
    dashboardPagesPaths.map((pagePath) =>
      loadDashboardPageComponent(projectFolder, pagePath)
    )
  );

  const validateDashboardRoute = createDashboardRouteValidation(dashboardPages);

  for (const { route } of dashboardPages) {
    const result = validateDashboardRoute(route);

    if (!result.valid) {
      throw new CliError({
        code: CliErrorCode.InvalidDashboardPageRoute({
          route,
          errorMessage: result.reason,
        }),
        cause: null,
      });
    }
  }

  return dashboardPages;
}

async function loadDashboardPluginComponent(
  pluginPath: string
): Promise<DevCenterComponent<'DashboardPlugin'>> {
  const pluginConfig = await loadDashboardPluginComponentConfig(pluginPath);

  return DevCenterComponent.DashboardPlugin({
    ...pluginConfig,
    relativePath: pluginPath,
    platforms: [PlatformType.Dashboard()],
  });
}

export async function loadDashboardPluginComponents(
  projectFolder: string
): Promise<Array<DevCenterComponent<'DashboardPlugin'>>> {
  const pluginPaths = await globby(
    normalizeGlobbyPattern(DASHBOARD_PLUGINS_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const plugins = await Promise.all(
    pluginPaths.map(loadDashboardPluginComponent)
  );

  return plugins;
}

async function loadDashboardMenuPluginComponent(
  menuPluginPath: string
): Promise<DevCenterComponent<'DashboardMenuPlugin'>> {
  const menuPluginConfig =
    await loadDashboardMenuPluginComponentConfig(menuPluginPath);

  return DevCenterComponent.DashboardMenuPlugin({
    ...menuPluginConfig,
    relativePath: menuPluginPath,
    platforms: [PlatformType.Dashboard()],
  });
}

async function loadDashboardModalComponent(
  modalPath: string
): Promise<DevCenterComponent<'DashboardModal'>> {
  const modalConfig = await loadDashboardModalComponentConfig(modalPath);

  return DevCenterComponent.DashboardModal({
    ...modalConfig,
    relativePath: modalPath,
    platforms: [PlatformType.Dashboard()],
    route: '/',
  });
}

export async function loadDashboardMenuPluginComponents(
  projectFolder: string
): Promise<Array<DevCenterComponent<'DashboardMenuPlugin'>>> {
  const menuPluginPaths = await globby(
    normalizeGlobbyPattern(DASHBOARD_MENU_PLUGINS_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const menuPlugins = await Promise.all(
    menuPluginPaths.map(loadDashboardMenuPluginComponent)
  );

  return menuPlugins;
}

export async function loadDashboardModalComponents(
  projectFolder: string
): Promise<Array<DevCenterComponent<'DashboardModal'>>> {
  const modalPaths = await globby(
    normalizeGlobbyPattern(DASHBOARD_MODALS_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const modals = await Promise.all(
    modalPaths.map((modalPath) => loadDashboardModalComponent(modalPath))
  );

  return modals;
}

export function loadDashboardSidebarCategoryComponent(
  config: WixConfig
): Array<DevCenterComponent<'DashboardSidebarCategory'>> {
  return (
    config.dashboard?.sidebar?.categories?.map((sidebarCategory) =>
      DevCenterComponent.DashboardSidebarCategory({
        ...sidebarCategory,
        platforms: [],
      })
    ) ?? []
  );
}

export async function createTopologyComponent(
  projectFolder: string
): Promise<DevCenterComponent<'Topology'>> {
  const appConfig = await readAppConfig(projectFolder);
  const id = appConfig?.topologyComponentId ?? randomUUID();

  return DevCenterComponent.Topology({
    id,
    name: 'Topology',
    platforms: [],
    backendApiUrl: API_BASEURL_PLACEHOLDER,
  });
}

export async function loadMonitoringComponents(
  projectFolder: string,
  config: WixConfig
): Promise<Array<DevCenterComponent<'Monitoring'>>> {
  const monitoringConfig = config.monitoring;
  if (!monitoringConfig) {
    return [];
  }

  const appConfig = await readAppConfig(projectFolder);
  const id = appConfig?.monitoringComponentId ?? randomUUID();

  const monitoringComponent = DevCenterComponent.Monitoring({
    ...monitoringConfig,
    id,
    platforms: [],
  });

  return [monitoringComponent];
}
